import { AcademyApiError, browserSession, logout } from "./academyApi";
import { bindIdentitySession, identitySnapshot, revalidateIdentitySession } from "./identityClient";
import { workspaceClients, type WorkspaceClient } from "./workspaceAccess";
import { notifyIdentitySessionChange, watchIdentitySession } from "./generated/identitySession";

export interface FrontendSession<App extends string = string> {
  version: 1;
  subject: string;
  name: string;
  email: string;
  audience: App;
  role: string;
  roles: string[];
  tenantId: string;
  workspaceId: string;
  permissions: string[];
  mfaVerifiedAt: string | null;
}
export function createFrontendSession<App extends string>(app: App, appUrl: (target: "auth") => string) {
  let stopWatching: (() => void) | undefined;
  const getSession = (): FrontendSession<App> | null => {
    const data = identitySnapshot().session;
    if (!data) return null;
    return {
      version: 1,
      subject: data.user.id,
      name: data.user.name,
      email: data.user.email,
      audience: app,
      role:
        data.roles.find(role => workspaceClients([role]).includes(app as WorkspaceClient)) || data.roles[0],
      roles: data.roles,
      tenantId: data.tenantId,
      workspaceId: data.workspace.id,
      permissions: data.permissions,
      mfaVerifiedAt: data.mfaVerifiedAt,
    };
  };
  const clearSession = () => {
    stopWatching?.();
    stopWatching = undefined;
    bindIdentitySession(null);
  };
  const sessionScope = (session: ReturnType<typeof identitySnapshot>["session"]) =>
    session
      ? JSON.stringify([
          session.user.id,
          session.tenantId,
          session.workspace.id,
          [...session.roles].sort(),
          [...session.permissions].sort(),
        ])
      : null;
  async function requireFrontendSession() {
    try {
      localStorage.removeItem(`chatoner-academy:${app}:session:v1`);
    } catch {
      /* Cookie sessions do not require browser storage. */
    }
    const current = new URL(window.location.href);
    for (const key of ["auth_code", "code", "state"]) current.searchParams.delete(key);
    window.history.replaceState(window.history.state, "", current.pathname + current.search + current.hash);
    try {
      const data = await browserSession();
      if (!workspaceClients(data.roles).includes(app as WorkspaceClient))
        throw new AcademyApiError(
          403,
          "This account has no assigned role for this platform. Open Academy Access to select an assigned workspace.",
        );
      bindIdentitySession(data);
      stopWatching?.();
      stopWatching = watchIdentitySession(
        async isCurrent => {
          const expected = identitySnapshot().session;
          if (!expected) return;
          const current = await browserSession();
          if (!isCurrent()) return;
          if (
            sessionScope(expected) !== sessionScope(current) ||
            !workspaceClients(current.roles).includes(app as WorkspaceClient)
          ) {
            clearSession();
            window.location.replace(appUrl("auth"));
          } else {
            // A same-context check must not erase an in-progress profile form or one-time codes.
            if (isCurrent()) revalidateIdentitySession(current);
          }
        },
        error => {
          if (error instanceof AcademyApiError && [401, 403].includes(error.status)) {
            clearSession();
            window.location.replace(appUrl("auth"));
          }
        },
      );
      return getSession();
    } catch (error) {
      clearSession();
      if (!(error instanceof AcademyApiError) || error.status !== 401) throw error;
      const target = new URL(appUrl("auth"));
      target.searchParams.set("client_id", app);
      target.searchParams.set("return_to", current.toString());
      target.searchParams.set("state", crypto.randomUUID());
      window.location.replace(target.toString());
      return null;
    }
  }
  async function signOut() {
    await logout();
    notifyIdentitySessionChange();
    clearSession();
    window.location.assign(appUrl("auth"));
  }
  return { getSession, clearSession, requireFrontendSession, signOut };
}
